import * as React from 'react';
import { v4 as uuid } from 'uuid';
import { noop } from 'vet-bones/utils';

import {
  ProjectFileUploadField,
  ProjectFileUploadOptions,
  ProjectFileUploadStatistics,
} from 'src/store/state/projects';
import { assignDistinctAndRecordStatistics } from 'src/utils/csv/assignDistinctAndRecordStatistics';
import { ExtendedParseError, parseCsv } from 'src/utils/csv/parseCsv';

export const useProjectFileUploadParser = (
  file: string | File | null,
  fileOptions: ProjectFileUploadOptions,
  limitRows?: number,
  existingFields?: ProjectFileUploadField[]
) => {
  const [errors, setErrors] = React.useState<ExtendedParseError[]>([]);
  const [fields, setFields] = React.useState<ProjectFileUploadField[]>(
    existingFields || []
  );
  const [rows, setRows] = React.useState<string[][]>([]);

  const parsingIds = React.useRef<Set<string>>(new Set());
  const [isParsing, setIsParsing] = React.useState(false);

  const didCleanup = React.useRef<boolean>(false);
  const abortParser = React.useRef<() => void>(noop);

  React.useEffect(() => {
    didCleanup.current = false;
    abortParser.current();

    if (file && fileOptions.quote && fileOptions.separator) {
      const id = uuid();
      parsingIds.current.add(id);
      setIsParsing(true);
      const { abort, parsePromise } = parseCsv<ProjectFileUploadStatistics>(
        file,
        {
          delimiter: fileOptions.separator,
          escapeChar: fileOptions.escape,
          header: fileOptions.header,
          quoteChar: fileOptions.quote,
        },
        { distinct: 0, records: 0 },
        assignDistinctAndRecordStatistics,
        limitRows
      );

      abortParser.current = abort;
      parsePromise.then(({ data, errors }) => {
        if (didCleanup.current) {
          return;
        }

        if (errors) {
          setErrors(errors);
          setFields([]);
          setRows([]);
        } else if (data) {
          setErrors([]);
          setFields(
            data.fields.map((newField, index) => ({
              ...newField,
              label:
                existingFields?.[index]?.value === newField.value
                  ? existingFields[index].label
                  : newField.label,
            }))
          );
          setRows(data.rows);
        }
        parsingIds.current.delete(id);
        setIsParsing(Boolean(parsingIds.current.size));
      });
    } else {
      abortParser.current = noop;
      setFields([]);
      setRows([]);
    }

    return () => {
      didCleanup.current = true;
      abortParser.current();
    };
  }, [
    existingFields,
    file,
    fileOptions.escape,
    fileOptions.header,
    fileOptions.quote,
    fileOptions.separator,
    limitRows,
  ]);

  const updateFieldLabel = React.useCallback(
    (value: string, label: string) => {
      setFields(
        fields.map((field) => ({
          ...field,
          label: field.value === value ? label : field.label,
        }))
      );
    },
    [fields]
  );

  return {
    abortParser,
    errors,
    fields,
    isParsing,
    rows,
    updateFieldLabel,
  };
};
